import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { loadSavedTextTemplates, type SavedTextTemplate } from '../utils/savedTextTemplates';

type MandateStatus = 'Pendente' | 'Cumprido' | 'Atrasado';
type MandatePriority = 'Normal' | 'Alta' | 'Urgente';
type MandateType = 'Citação' | 'Intimação' | 'Citação + Intimação' | 'Convocação para júri' | 'Outros';

interface Mandate {
  id: string;
  recipientName: string;
  address: string;
  region: string;
  attachmentDate: string;
  phones?: string[];
  reason?: string;
  caseNumber: string;
  mandateType: MandateType;
  priority: MandatePriority;
  mandateDate: string;
  hearingDate: string;
  status: MandateStatus;
  confidential: boolean;
  aiSummary: string;
  messageTemplate: string;
  distributionDate: string;
}

interface ExtractedMandateDraft {
  caseNumber: string;
  distributionDate: string;
  hearingDate: string;
  recipientName: string;
  address: string;
  phones: string[];
  reason: string;
  mandateType: MandateType;
  summary: string;
  nextAction: string;
  generatedMessage: string;
  provider?: string;
}

interface WhatsAppSession {
  name: string;
  displayName?: string;
  mePushName?: string | null;
}

interface MandateCampaignAction {
  id: string;
  nome: string;
  status: string;
  totalContacts: number;
  sentCount: number;
  failedCount: number;
  criadoEm: string;
  _count?: {
    messages: number;
  };
}

const mandatesSeed: Mandate[] = [
  {
    id: 'mandado-1',
    recipientName: 'Mariana Alves de Souza',
    address: 'Rua das Acacias, 184, Apto 32, Centro, Campinas/SP',
    region: 'Campinas/SP',
    attachmentDate: '2026-04-26',
    caseNumber: '1002847-53.2026.8.26.0114',
    mandateType: 'Intimação',
    priority: 'Urgente',
    mandateDate: '2026-04-21',
    hearingDate: '2026-04-28',
    status: 'Pendente',
    confidential: true,
    distributionDate: '2026-04-18',
    aiSummary: 'Mandado de intimacao para comparecimento em audiencia de instrucao. Exige contato imediato e confirmacao de recebimento devido ao prazo curto.',
    messageTemplate: 'Ola, Mariana. Sou da Central de Mandados e preciso confirmar o recebimento da intimacao referente ao processo 1002847-53.2026.8.26.0114. Posso lhe encaminhar o resumo e as orientacoes por aqui?'
  },
  {
    id: 'mandado-2',
    recipientName: 'Carlos Henrique Lima',
    address: 'Avenida Presidente Vargas, 950, Sala 08, Vila Nova, Ribeirao Preto/SP',
    region: 'Ribeirao Preto/SP',
    attachmentDate: '2026-04-26',
    caseNumber: '5009134-12.2026.8.26.0506',
    mandateType: 'Citação + Intimação',
    priority: 'Alta',
    mandateDate: '2026-04-15',
    hearingDate: '2026-05-03',
    status: 'Pendente',
    confidential: false,
    distributionDate: '2026-04-12',
    aiSummary: 'Mandado combinado com citacao e intimacao. A pessoa deve ser cientificada da acao e da audiencia, com destaque para a apresentacao de defesa.',
    messageTemplate: 'Carlos, ha um mandado de citacao e intimacao vinculado ao processo 5009134-12.2026.8.26.0506. Posso lhe passar um resumo objetivo e as proximas providencias?'
  },
  {
    id: 'mandado-3',
    recipientName: 'Fernanda Oliveira Batista',
    address: 'Rua Professor Joao Mendes, 72, Jardim America, Sao Paulo/SP',
    region: 'Sao Paulo/SP',
    attachmentDate: '2026-04-25',
    caseNumber: '0027419-88.2025.8.26.0100',
    mandateType: 'Citação',
    priority: 'Normal',
    mandateDate: '2026-04-05',
    hearingDate: '2026-04-17',
    status: 'Cumprido',
    confidential: false,
    distributionDate: '2026-04-01',
    aiSummary: 'Citacao regularmente cumprida, com ciencia da destinataria e registro de entrega concluido dentro do prazo.',
    messageTemplate: 'Fernanda, registramos o cumprimento do mandado referente ao processo 0027419-88.2025.8.26.0100. Se desejar, posso reenviar o resumo do conteudo.'
  },
  {
    id: 'mandado-4',
    recipientName: 'Rogerio Martins Pacheco',
    address: 'Travessa Santo Expedito, 41, Parque das Flores, Sorocaba/SP',
    region: 'Sorocaba/SP',
    attachmentDate: '2026-04-25',
    caseNumber: '1017742-05.2026.8.26.0602',
    mandateType: 'Convocação para júri',
    priority: 'Urgente',
    mandateDate: '2026-04-10',
    hearingDate: '2026-04-24',
    status: 'Atrasado',
    confidential: true,
    distributionDate: '2026-04-07',
    aiSummary: 'Convocacao para juri com prazo vencido. Precisa de tratamento prioritario, revisao operacional e nova tentativa de contato documentada.',
    messageTemplate: 'Rogerio, ha pendencia urgente referente a convocacao para juri do processo 1017742-05.2026.8.26.0602. Preciso falar com voce ainda hoje para alinharmos o comparecimento.'
  },
  {
    id: 'mandado-5',
    recipientName: 'Juliana Costa Ribeiro',
    address: 'Alameda dos Ipes, 210, Bloco B, Jardim Europa, Bauru/SP',
    region: 'Bauru/SP',
    attachmentDate: '2026-04-24',
    caseNumber: '0901182-61.2026.8.26.0071',
    mandateType: 'Outros',
    priority: 'Alta',
    mandateDate: '2026-04-22',
    hearingDate: '2026-04-30',
    status: 'Pendente',
    confidential: false,
    distributionDate: '2026-04-20',
    aiSummary: 'Mandado com determinacao complementar de comparecimento e apresentacao de documentos. Exige confirmacao do endereco e lembrete pre-audiencia.',
    messageTemplate: 'Juliana, localizamos um mandado com orientacoes importantes no processo 0901182-61.2026.8.26.0071. Posso lhe enviar um resumo claro e os documentos que precisam ser levados?'
  },
  {
    id: 'mandado-6',
    recipientName: 'Eduardo Nascimento Prado',
    address: 'Rua Minas Gerais, 1340, Centro, Uberaba/MG',
    region: 'Uberaba/MG',
    attachmentDate: '2026-04-24',
    caseNumber: '0703310-44.2026.8.13.0701',
    mandateType: 'Intimação',
    priority: 'Normal',
    mandateDate: '2026-04-19',
    hearingDate: '2026-05-10',
    status: 'Pendente',
    confidential: false,
    distributionDate: '2026-04-16',
    aiSummary: 'Intimacao para audiencia futura, sem urgencia imediata, mas com necessidade de contato preventivo e registro de ciencia.',
    messageTemplate: 'Eduardo, preciso confirmar o recebimento da intimacao do processo 0703310-44.2026.8.13.0701. Posso lhe encaminhar um resumo com data e orientacoes da audiencia?'
  }
];

const extractedDraftSeed: ExtractedMandateDraft = {
  caseNumber: '1009458-21.2026.8.26.0100',
  distributionDate: '2026-04-24',
  hearingDate: '2026-05-02',
  recipientName: 'Patricia Gomes de Almeida',
  address: 'Rua Conselheiro Crispiniano, 455, Bela Vista, Sao Paulo/SP',
  phones: ['5561999998888'],
  reason: 'Citacao e intimacao para comparecimento em audiencia e ciencia da acao.',
  mandateType: 'Citação + Intimação',
  summary: 'A IA identificou um mandado de citacao e intimacao com audiencia proxima. O documento informa comparecimento obrigatorio, ciencia formal da acao e necessidade de atencao imediata ao prazo.',
  nextAction: 'Confirmar endereco, priorizar contato por WhatsApp e registrar tentativa ainda hoje.',
  generatedMessage: 'Aqui e o oficial de justica, Usuario Teste, encontro-me com um mandado destinado a Patricia Gomes de Almeida, posso encaminhar por aqui o mandado?'
};

const statusOptions: Array<'Todos' | MandateStatus> = ['Todos', 'Pendente', 'Cumprido', 'Atrasado'];
const priorityOptions: Array<'Todas' | MandatePriority> = ['Todas', 'Normal', 'Alta', 'Urgente'];
const typeOptions: Array<'Todos' | MandateType> = ['Todos', 'Citação', 'Intimação', 'Citação + Intimação', 'Convocação para júri', 'Outros'];

const formatDate = (value: string) => {
  if (!value) return '-';
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime())
    ? value
    : new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(parsed);
};

const formatLongDate = (value: string) => {
  if (!value) return '-';
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime())
    ? value
    : new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }).format(parsed);
};

const getDaysUntil = (value: string) => {
  if (!value) return Number.NaN;
  const today = new Date();
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const target = new Date(`${value}T12:00:00`);
  return Math.ceil((target.getTime() - base.getTime()) / (1000 * 60 * 60 * 24));
};

const getStatusClass = (status: MandateStatus) => {
  if (status === 'Cumprido') return 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200';
  if (status === 'Atrasado') return 'bg-rose-100 text-rose-800 ring-1 ring-rose-200';
  return 'bg-slate-100 text-slate-700 ring-1 ring-slate-200';
};

const getPriorityClass = (priority: MandatePriority) => {
  if (priority === 'Urgente') return 'bg-rose-600 text-white';
  if (priority === 'Alta') return 'bg-amber-500 text-white';
  return 'bg-slate-200 text-slate-700';
};

const getPhoneDigits = (value: string) => value.replace(/\D/g, '');

const normalizePhoneForBr = (value: string): string => {
  const digits = getPhoneDigits(value);

  if (!digits) return '';

  if (digits.startsWith('55')) {
    return digits;
  }

  if (digits.length === 10 || digits.length === 11) {
    return `55${digits}`;
  }

  return digits;
};

const formatPhoneWithMask = (value: string): string => {
  const normalized = normalizePhoneForBr(value);
  if (!normalized) return '';

  const withoutCountryCode = normalized.startsWith('55') ? normalized.slice(2) : normalized;
  const ddd = withoutCountryCode.slice(0, 2);
  const local = withoutCountryCode.slice(2);

  if (!ddd) return normalized;

  if (local.length >= 9) {
    return `+55 (${ddd}) ${local.slice(0, 5)}-${local.slice(5, 9)}`;
  }

  if (local.length >= 8) {
    return `+55 (${ddd}) ${local.slice(0, 4)}-${local.slice(4, 8)}`;
  }

  if (local.length > 0) {
    return `+55 (${ddd}) ${local}`;
  }

  return `+55 (${ddd})`;
};

const normalizeStatus = (mandate: Mandate): MandateStatus => {
  if (mandate.status === 'Cumprido') return 'Cumprido';
  return getDaysUntil(mandate.hearingDate) < 0 ? 'Atrasado' : mandate.status;
};

const mapBackendStatusToUi = (status?: string, hearingDate?: string): MandateStatus => {
  if (status === 'cumprido') return 'Cumprido';
  if (status === 'nao_cumprido') return 'Atrasado';
  if (hearingDate && getDaysUntil(hearingDate) < 0) return 'Atrasado';
  return 'Pendente';
};

const mapUiStatusToBackend = (status: MandateStatus): string => {
  if (status === 'Cumprido') return 'cumprido';
  if (status === 'Atrasado') return 'nao_cumprido';
  return 'novo';
};

const inferPriority = (hearingDate: string): MandatePriority => {
  const days = getDaysUntil(hearingDate);
  if (Number.isNaN(days)) return 'Normal';
  if (days <= 3) return 'Urgente';
  if (days <= 7) return 'Alta';
  return 'Normal';
};

const mapApiMandateToUi = (mandate: any): Mandate => {
  const extracted = mandate?.dadosExtraidos || {};
  const hearingDate = extracted.hearingDate || '';
  const recipientName = mandate?.nomeDestinatario || extracted.recipientName || 'Destinatario nao identificado';
  const address = mandate?.endereco || extracted.address || '-';
  const contactPhones = Array.isArray(mandate?.contacts) ? mandate.contacts.map((contact: any) => contact?.telefone) : [];
  const extractedPhones = Array.isArray(extracted.phones) ? extracted.phones : [];
  const phones = Array.from(
    new Set(
      [...extractedPhones, ...contactPhones, mandate?.telefone]
        .map((phone) => (typeof phone === 'string' ? normalizePhoneForBr(phone.trim()) : ''))
        .filter(Boolean)
    )
  ).map((phone) => formatPhoneWithMask(phone));

  return {
    id: mandate.id,
    recipientName,
    address,
    region: address.split(',').slice(-1)[0]?.trim() || 'Nao definida',
    attachmentDate: String(mandate.createdAt || '').slice(0, 10),
    phones,
    reason: extracted.reason || '',
    caseNumber: extracted.caseNumber || '',
    mandateType: (extracted.mandateType || 'Outros') as MandateType,
    priority: inferPriority(hearingDate),
    mandateDate: String(mandate.createdAt || '').slice(0, 10),
    hearingDate,
    status: mapBackendStatusToUi(mandate.status, hearingDate),
    confidential: Boolean(extracted.confidential),
    aiSummary: extracted.summary || '',
    messageTemplate: extracted.generatedMessage || '',
    distributionDate: extracted.distributionDate || ''
  };
};

const applyTemplateToMandate = (templateBody: string, mandate: Mandate) => {
  return templateBody
    .replace(/\{\{nome\}\}/g, mandate.recipientName || '')
    .replace(/\{\{telefone\}\}/g, mandate.phones?.[0] || '')
    .replace(/\{\{email\}\}/g, '')
    .replace(/\{\{cpf\}\}/g, '')
    .replace(/\{\{endereco\}\}/g, mandate.address || '')
    .replace(/\{\{processo\}\}/g, mandate.caseNumber || '')
    .replace(/\{\{mandado\}\}/g, mandate.mandateType || '')
    .replace(/\{\{observacoes\}\}/g, mandate.aiSummary || '')
    .replace(/\{\{categoria\}\}/g, 'Mandados IA');
};

const getCampaignStatusLabel = (status: string) => {
  if (['RUNNING', 'PENDING', 'PAUSED'].includes(status)) return 'Em execucao';
  if (status === 'COMPLETED') return 'Concluida';
  if (status === 'FAILED' || status === 'CANCELLED') return 'Interrompida';
  return status;
};

const getCampaignStatusClass = (status: string) => {
  if (['RUNNING', 'PENDING', 'PAUSED'].includes(status)) {
    return 'bg-amber-100 text-amber-800 ring-1 ring-amber-200';
  }

  if (status === 'COMPLETED') {
    return 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200';
  }

  return 'bg-rose-100 text-rose-800 ring-1 ring-rose-200';
};

export function MandatesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'Todos' | MandateStatus>('Todos');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState<'Todas' | MandatePriority>('Todas');
  const [typeFilter, setTypeFilter] = useState<'Todos' | MandateType>('Todos');
  const [regionFilter, setRegionFilter] = useState('Todas');
  const [selectedQueueDate, setSelectedQueueDate] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [view, setView] = useState<'list' | 'upload'>('list');
  const [selectedMandate, setSelectedMandate] = useState<Mandate | null>(null);
  const [detailsMandate, setDetailsMandate] = useState<Mandate | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadFileName, setUploadFileName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingMandates, setIsLoadingMandates] = useState(false);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isCreatingBulkAction, setIsCreatingBulkAction] = useState(false);
  const [processedDraft, setProcessedDraft] = useState<ExtractedMandateDraft | null>(null);
  const [draftSummary, setDraftSummary] = useState('');
  const [draftWhatsAppMessage, setDraftWhatsAppMessage] = useState('');
  const [mandates, setMandates] = useState<Mandate[]>(mandatesSeed);
  const [selectedMandateIds, setSelectedMandateIds] = useState<string[]>([]);
  const [savedTemplates, setSavedTemplates] = useState<SavedTextTemplate[]>([]);
  const [draftTemplateId, setDraftTemplateId] = useState('');
  const [whatsAppSessions, setWhatsAppSessions] = useState<WhatsAppSession[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [messageDraft, setMessageDraft] = useState('');
  const [selectedSessionName, setSelectedSessionName] = useState('');
  const [showBulkComposer, setShowBulkComposer] = useState(false);
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const [bulkMessageDraft, setBulkMessageDraft] = useState('');
  const [bulkSessionName, setBulkSessionName] = useState('');
  const [mandateCampaigns, setMandateCampaigns] = useState<MandateCampaignAction[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [editingPhonesByMandate, setEditingPhonesByMandate] = useState<Record<string, string[]>>({});
  const [savingPhonesByMandate, setSavingPhonesByMandate] = useState<Record<string, boolean>>({});

  const loadMandateCampaigns = useCallback(async () => {
    try {
      setIsLoadingCampaigns(true);
      const response = await apiService.listCampaigns({ page: 1, limit: 20 });
      const campaigns = Array.isArray(response?.campaigns) ? response.campaigns : [];
      const mandateOnly = campaigns.filter((campaign: MandateCampaignAction) => campaign.nome.startsWith('Mandados IA'));
      setMandateCampaigns(mandateOnly);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingCampaigns(false);
    }
  }, []);

  const loadWhatsAppSessions = useCallback(async () => {
    try {
      const response = await apiService.getCampaignSessions();
      const sessions = Array.isArray(response) ? response : [];
      setWhatsAppSessions(sessions);
      setSelectedSessionName((current) => current || sessions[0]?.name || '');
      setBulkSessionName((current) => current || sessions[0]?.name || '');
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    const loadMandates = async () => {
      try {
        setIsLoadingMandates(true);
        const response = await apiService.listMandates({ page: 1, pageSize: 100 });
        const nextMandates = Array.isArray(response?.mandates)
          ? response.mandates.map(mapApiMandateToUi)
          : [];

        if (nextMandates.length > 0 || response?.total === 0) {
          setMandates(nextMandates);
        }
      } catch (error) {
        console.error(error);
        toast.error('Nao foi possivel carregar os mandados reais. Mantendo a visao atual.');
      } finally {
        setIsLoadingMandates(false);
      }
    };

    loadMandates();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    setSavedTemplates(
      loadSavedTextTemplates({
        userId: user?.id,
        tenantId: user?.tenantId || null,
        token
      })
    );
  }, [user?.id, user?.tenantId]);

  useEffect(() => {
    loadWhatsAppSessions();
    loadMandateCampaigns();
  }, [loadMandateCampaigns, loadWhatsAppSessions]);

  const regions = useMemo(() => ['Todas', ...Array.from(new Set(mandates.map((mandate) => mandate.region)))], [mandates]);

  const filteredMandates = useMemo(() => {
    const query = search.trim().toLowerCase();

    return mandates
      .map((mandate) => ({ ...mandate, status: normalizeStatus(mandate) }))
      .filter((mandate) => {
        const matchesQuery =
          !query ||
          mandate.recipientName.toLowerCase().includes(query) ||
          mandate.caseNumber.toLowerCase().includes(query) ||
          mandate.region.toLowerCase().includes(query);

        const matchesStatus = statusFilter === 'Todos' || mandate.status === statusFilter;
        const matchesPriority = priorityFilter === 'Todas' || mandate.priority === priorityFilter;
        const matchesType = typeFilter === 'Todos' || mandate.mandateType === typeFilter;
        const matchesRegion = regionFilter === 'Todas' || mandate.region === regionFilter;
        const matchesDate = !dateFilter || mandate.hearingDate === dateFilter || mandate.mandateDate === dateFilter;

        return matchesQuery && matchesStatus && matchesPriority && matchesType && matchesRegion && matchesDate;
      })
      .sort((a, b) => new Date(a.hearingDate).getTime() - new Date(b.hearingDate).getTime());
  }, [mandates, search, statusFilter, priorityFilter, typeFilter, regionFilter, dateFilter]);

  const queueGroups = useMemo(() => {
    const grouped = filteredMandates.reduce<Record<string, typeof filteredMandates>>((acc, mandate) => {
      if (!acc[mandate.attachmentDate]) {
        acc[mandate.attachmentDate] = [];
      }
      acc[mandate.attachmentDate].push(mandate);
      return acc;
    }, {});

    return Object.entries(grouped)
      .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime())
      .map(([attachmentDate, items]) => ({
        attachmentDate,
        items,
        total: items.length,
        urgent: items.filter((item) => item.priority === 'Urgente').length,
        overdue: items.filter((item) => item.status === 'Atrasado').length
      }));
  }, [filteredMandates]);

  const activeQueueDate = selectedQueueDate && queueGroups.some((queue) => queue.attachmentDate === selectedQueueDate)
    ? selectedQueueDate
    : null;

  const visibleMandates = activeQueueDate
    ? filteredMandates.filter((mandate) => mandate.attachmentDate === activeQueueDate)
    : [];

  const selectedMandates = useMemo(
    () => mandates.filter((mandate) => selectedMandateIds.includes(mandate.id)),
    [mandates, selectedMandateIds]
  );

  const selectedVisibleCount = useMemo(
    () => visibleMandates.filter((mandate) => selectedMandateIds.includes(mandate.id)).length,
    [selectedMandateIds, visibleMandates]
  );

  const totalPages = Math.max(1, Math.ceil(visibleMandates.length / itemsPerPage));
  const currentItems = visibleMandates.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    if (!selectedMandate) return;
    setMessageDraft(selectedMandate.messageTemplate || '');
    setSelectedTemplateId('');
  }, [selectedMandate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, activeQueueDate]);

  useEffect(() => {
    if (!showBulkComposer) return;
    const baseText = selectedMandates[0]?.messageTemplate || '';
    setBulkMessageDraft(baseText);
    setBulkTemplateId('');
  }, [showBulkComposer, selectedMandates]);

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('Todos');
    setPriorityFilter('Todas');
    setTypeFilter('Todos');
    setRegionFilter('Todas');
    setSelectedQueueDate(null);
    setDateFilter('');
    setCurrentPage(1);
  };

  const toggleMandateSelection = (mandateId: string) => {
    setSelectedMandateIds((current) =>
      current.includes(mandateId) ? current.filter((id) => id !== mandateId) : [...current, mandateId]
    );
  };

  const toggleVisibleSelection = () => {
    const visibleIds = visibleMandates.map((mandate) => mandate.id);
    const allVisibleSelected = visibleIds.every((id) => selectedMandateIds.includes(id));

    setSelectedMandateIds((current) => {
      if (allVisibleSelected) {
        return current.filter((id) => !visibleIds.includes(id));
      }

      return Array.from(new Set([...current, ...visibleIds]));
    });
  };

  const handleApplyTemplateToWhatsApp = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = savedTemplates.find((item) => item.id === templateId);
    if (!template || !selectedMandate) return;
    setMessageDraft(applyTemplateToMandate(template.body, selectedMandate));
    toast.success('Modelo de envio aplicado ao mandado.');
  };

  const handleApplyTemplateToBulkComposer = (templateId: string) => {
    setBulkTemplateId(templateId);
    const template = savedTemplates.find((item) => item.id === templateId);
    if (!template || selectedMandates.length === 0) return;
    setBulkMessageDraft(applyTemplateToMandate(template.body, selectedMandates[0]));
    toast.success('Modelo de envio em massa aplicado.');
  };

  const handleApplyTemplateToDraftMessage = (templateId: string) => {
    setDraftTemplateId(templateId);
    const template = savedTemplates.find((item) => item.id === templateId);
    if (!template || !processedDraft) return;

    const draftMandate: Mandate = {
      id: 'draft',
      recipientName: processedDraft.recipientName,
      address: processedDraft.address,
      region: processedDraft.address.split(',').slice(-1)[0]?.trim() || 'Nao definida',
      attachmentDate: new Date().toISOString().slice(0, 10),
      phones: processedDraft.phones,
      reason: processedDraft.reason,
      caseNumber: processedDraft.caseNumber,
      mandateType: processedDraft.mandateType,
      priority: inferPriority(processedDraft.hearingDate),
      mandateDate: new Date().toISOString().slice(0, 10),
      hearingDate: processedDraft.hearingDate,
      status: getDaysUntil(processedDraft.hearingDate) < 0 ? 'Atrasado' : 'Pendente',
      confidential: false,
      aiSummary: draftSummary || processedDraft.summary,
      messageTemplate: draftWhatsAppMessage || processedDraft.generatedMessage,
      distributionDate: processedDraft.distributionDate
    };

    setDraftWhatsAppMessage(applyTemplateToMandate(template.body, draftMandate));
    toast.success('Mensagem salva aplicada ao texto do oficial.');
  };

  const handleCopyMessage = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Mensagem copiada para a area de transferencia.');
    } catch (error) {
      console.error(error);
      toast.error('Nao foi possivel copiar a mensagem.');
    }
  };

  const getEditablePhones = (mandate: Mandate): string[] => {
    const draftPhones = editingPhonesByMandate[mandate.id];
    if (draftPhones) {
      return draftPhones;
    }

    if (mandate.phones && mandate.phones.length > 0) {
      return mandate.phones;
    }

    return [''];
  };

  const startPhoneEditing = (mandate: Mandate) => {
    setEditingPhonesByMandate((current) => ({
      ...current,
      [mandate.id]: mandate.phones && mandate.phones.length > 0 ? [...mandate.phones] : ['']
    }));
  };

  const updateEditablePhone = (mandateId: string, index: number, value: string) => {
    setEditingPhonesByMandate((current) => ({
      ...current,
      [mandateId]: (current[mandateId] || ['']).map((phone, phoneIndex) => (
        phoneIndex === index ? formatPhoneWithMask(value) : phone
      ))
    }));
  };

  const addEditablePhone = (mandateId: string) => {
    setEditingPhonesByMandate((current) => ({
      ...current,
      [mandateId]: [...(current[mandateId] || ['']), '']
    }));
  };

  const removeEditablePhone = (mandateId: string, index: number) => {
    setEditingPhonesByMandate((current) => {
      const nextPhones = (current[mandateId] || ['']).filter((_, phoneIndex) => phoneIndex !== index);
      return {
        ...current,
        [mandateId]: nextPhones.length > 0 ? nextPhones : ['']
      };
    });
  };

  const cancelPhoneEditing = (mandateId: string) => {
    setEditingPhonesByMandate((current) => {
      const nextState = { ...current };
      delete nextState[mandateId];
      return nextState;
    });
  };

  const saveMandatePhones = async (mandate: Mandate) => {
    const editedPhones = (editingPhonesByMandate[mandate.id] || [])
      .map((phone) => formatPhoneWithMask(phone.trim()))
      .filter(Boolean);

    if (editedPhones.length === 0) {
      toast.error('Informe pelo menos um telefone para salvar.');
      return;
    }

    const uniquePhones = Array.from(new Set(editedPhones));
    const normalizedPhones = uniquePhones.map((phone) => normalizePhoneForBr(phone));

    try {
      setSavingPhonesByMandate((current) => ({ ...current, [mandate.id]: true }));
      const updated = await apiService.updateMandate(mandate.id, {
        nomeDestinatario: mandate.recipientName,
        endereco: mandate.address,
        telefone: normalizedPhones[0] || null,
        status: mapUiStatusToBackend(mandate.status),
        dadosExtraidos: {
          caseNumber: mandate.caseNumber,
          distributionDate: mandate.distributionDate,
          hearingDate: mandate.hearingDate,
          recipientName: mandate.recipientName,
          address: mandate.address,
          phones: uniquePhones,
          reason: mandate.reason || '',
          mandateType: mandate.mandateType,
          summary: mandate.aiSummary,
          generatedMessage: mandate.messageTemplate
        }
      });

      const nextMandate = mapApiMandateToUi(updated);
      setMandates((current) => current.map((item) => (item.id === mandate.id ? nextMandate : item)));
      cancelPhoneEditing(mandate.id);
      toast.success(uniquePhones.length > 1 ? 'Telefones atualizados. O envio usara todos eles.' : 'Telefone atualizado com sucesso.');
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar telefones do mandado.');
    } finally {
      setSavingPhonesByMandate((current) => ({ ...current, [mandate.id]: false }));
    }
  };

  const handleUseSummaryInWhatsAppDraft = () => {
    if (!draftSummary.trim()) {
      toast.error('Edite ou gere um resumo antes de levá-lo para a mensagem.');
      return;
    }

    setDraftWhatsAppMessage((current) => {
      const trimmedCurrent = current.trim();
      const trimmedSummary = draftSummary.trim();

      if (!trimmedCurrent) {
        return trimmedSummary;
      }

      if (trimmedCurrent.includes(trimmedSummary)) {
        return current;
      }

      return `${trimmedCurrent}\n\nResumo do mandado:\n${trimmedSummary}`;
    });

    toast.success('Resumo do mandado levado para a mensagem de WhatsApp.');
  };

  const createMandateCampaignAction = async (params: {
    mandateIds: string[];
    message: string;
    sessionName: string;
    nome: string;
  }) => {
    if (!params.sessionName) {
      toast.error('Selecione uma conexao WhatsApp para criar a acao de envio.');
      return false;
    }

    if (!params.message.trim()) {
      toast.error('Escolha um modelo de mensagem antes de criar a acao.');
      return false;
    }

    try {
      setIsCreatingBulkAction(true);
      const response = await apiService.createCampaignFromMandates({
        nome: params.nome,
        mandateIds: params.mandateIds,
        sessionNames: [params.sessionName],
        messageType: 'text',
        messageContent: { text: params.message },
        randomDelay: 40,
        startImmediately: true,
        scheduledFor: null
      });

      const skipped = Array.isArray(response?.campaign?.skippedMandates) ? response.campaign.skippedMandates.length : 0;
      const totalTargets = Number(response?.campaign?.totalContacts || 0);
      toast.success(
        skipped > 0
          ? `Acao criada para ${totalTargets} telefone(s). ${skipped} mandado(s) ficaram pendentes por falta de telefone.`
          : `Acao de envio criada com sucesso para ${totalTargets} telefone(s).`
      );

      await loadMandateCampaigns();
      return true;
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Erro ao criar a acao de envio.');
      return false;
    } finally {
      setIsCreatingBulkAction(false);
    }
  };

  const handleDelete = (id: string) => {
    setMandates((current) => current.filter((mandate) => mandate.id !== id));
    setSelectedMandateIds((current) => current.filter((item) => item !== id));
    toast.success('Mandado excluido da fila de acompanhamento.');
    if (selectedMandate?.id === id) setSelectedMandate(null);
    if (detailsMandate?.id === id) setDetailsMandate(null);
  };

  const handleProcessPdf = async () => {
    if (!uploadFile) {
      toast.error('Selecione um PDF para iniciar a leitura com IA.');
      return;
    }

    setIsProcessing(true);
    setProcessedDraft(null);
    setDraftSummary('');
    setDraftWhatsAppMessage('');

    try {
      const response = await apiService.readMandatePdfWithAi(uploadFile);
      const data = response?.data || extractedDraftSeed;
      const nextDraft: ExtractedMandateDraft = {
        caseNumber: data.caseNumber || '',
        distributionDate: data.distributionDate || '',
        hearingDate: data.hearingDate || '',
        recipientName: data.recipientName || '',
        address: data.address || '',
        phones: Array.isArray(data.phones) ? Array.from(new Set(data.phones.map((phone: string) => formatPhoneWithMask(phone)))) : [],
        reason: data.reason || '',
        mandateType: (data.mandateType || 'Outros') as MandateType,
        summary: data.summary || '',
        nextAction: 'Confirmar os dados extraidos, validar telefone(s) e realizar o primeiro contato oficial.',
        generatedMessage: data.generatedMessage || '',
        provider: data.provider || undefined
      };

      setProcessedDraft(nextDraft);
      setDraftSummary(data.summary || 'Leitura concluida pela IA.');
      setDraftWhatsAppMessage(data.generatedMessage || '');
      setDraftTemplateId('');
      toast.success('PDF lido com sucesso. Os campos foram preenchidos automaticamente.');
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Erro ao ler o PDF com IA.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateFromDraft = async () => {
    if (!processedDraft) {
      toast.error('Faça a leitura do PDF antes de salvar o mandado.');
      return;
    }

    const draftStatus: MandateStatus = getDaysUntil(processedDraft.hearingDate) < 0 ? 'Atrasado' : 'Pendente';

    try {
      const createdMandate = await apiService.createMandate({
        arquivoPdf: uploadFileName || null,
        dadosExtraidos: {
          caseNumber: processedDraft.caseNumber,
          distributionDate: processedDraft.distributionDate,
          hearingDate: processedDraft.hearingDate,
          recipientName: processedDraft.recipientName,
          address: processedDraft.address,
          phones: processedDraft.phones,
          reason: processedDraft.reason,
          mandateType: processedDraft.mandateType,
          summary: draftSummary.trim() || processedDraft.summary,
          generatedMessage: draftWhatsAppMessage.trim() || processedDraft.generatedMessage
        },
        nomeDestinatario: processedDraft.recipientName,
        telefone: processedDraft.phones[0] || null,
        endereco: processedDraft.address,
        status: mapUiStatusToBackend(draftStatus),
        certificacaoTexto: null
      });

      const nextMandate = mapApiMandateToUi(createdMandate);
      setMandates((current) => [nextMandate, ...current.filter((item) => item.id !== nextMandate.id)]);
      setView('list');
      setUploadFile(null);
      setUploadFileName('');
      setProcessedDraft(null);
      setDraftSummary('');
      setDraftWhatsAppMessage('');
      setDraftTemplateId('');
      setCurrentPage(1);
      toast.success('Mandado salvo na nova camada central.');
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar mandado.');
    }
  };

  const renderCountdown = (date: string) => {
    const days = getDaysUntil(date);

    if (Number.isNaN(days)) {
      return <span className="text-slate-500">Data da audiencia nao identificada</span>;
    }

    if (days < 0) {
      return <span className="font-semibold text-rose-600">Vencido ha {Math.abs(days)} dia(s)</span>;
    }

    if (days <= 3) {
      return <span className="font-semibold text-amber-600">Audiencia em {days} dia(s)</span>;
    }

    return <span className="text-slate-600">Faltam {days} dia(s)</span>;
  };

  const renderMandateActions = (mandate: Mandate) => (
    <div className="flex items-center gap-2">
      <button type="button" onClick={() => setDetailsMandate(mandate)} className="rounded-full border border-slate-200 p-2 text-slate-600 transition hover:border-slate-300 hover:bg-slate-100 hover:text-slate-900" aria-label="Visualizar detalhes" title="Visualizar detalhes">
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12Z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
      <button type="button" onClick={() => setSelectedMandate(mandate)} className="rounded-full border border-emerald-200 p-2 text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50" aria-label="Enviar mensagem no WhatsApp" title="Enviar mensagem no WhatsApp">
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.52 3.48A11.8 11.8 0 0 0 12.05 0C5.6 0 .33 5.26.33 11.74c0 2.07.54 4.1 1.56 5.89L0 24l6.55-1.71a11.68 11.68 0 0 0 5.49 1.4h.01c6.45 0 11.72-5.27 11.72-11.75 0-3.13-1.22-6.07-3.25-8.46Zm-8.47 18.23h-.01a9.67 9.67 0 0 1-4.92-1.34l-.35-.21-3.89 1.01 1.04-3.79-.23-.39a9.74 9.74 0 0 1-1.49-5.2c0-5.38 4.38-9.76 9.77-9.76 2.61 0 5.06 1.02 6.9 2.87a9.66 9.66 0 0 1 2.85 6.89c0 5.39-4.38 9.78-9.77 9.78Zm5.36-7.31c-.29-.15-1.72-.85-1.98-.95-.27-.1-.46-.15-.65.15-.19.29-.75.95-.91 1.15-.17.19-.34.22-.63.07-.29-.15-1.22-.45-2.32-1.45-.86-.76-1.44-1.7-1.61-1.98-.17-.29-.02-.44.13-.58.13-.13.29-.34.44-.51.14-.17.19-.29.29-.49.1-.2.05-.37-.02-.52-.08-.15-.65-1.56-.89-2.14-.24-.56-.48-.48-.65-.49h-.56c-.2 0-.52.07-.79.37-.27.29-1.03 1.01-1.03 2.46 0 1.46 1.05 2.87 1.2 3.06.15.19 2.05 3.13 5.05 4.39.71.31 1.27.5 1.71.63.72.23 1.38.2 1.9.12.58-.09 1.72-.7 1.96-1.37.24-.68.24-1.26.17-1.38-.07-.12-.27-.2-.56-.35Z" />
        </svg>
      </button>
      <button type="button" onClick={() => handleCopyMessage(mandate.aiSummary || 'Resumo inteligente nao disponivel.')} className="rounded-full border border-sky-200 p-2 text-sky-700 transition hover:border-sky-300 hover:bg-sky-50" aria-label="Copiar resumo inteligente" title="Copiar resumo inteligente">
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      </button>
      <button type="button" onClick={() => handleDelete(mandate.id)} className="rounded-full border border-rose-200 p-2 text-rose-700 transition hover:border-rose-300 hover:bg-rose-50" aria-label="Excluir mandado" title="Excluir mandado">
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 6h18" />
          <path d="M8 6V4h8v2" />
          <path d="M19 6l-1 14H6L5 6" />
          <path d="M10 11v6M14 11v6" />
        </svg>
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(15,118,110,0.14),_transparent_38%),linear-gradient(180deg,_#f7fafc_0%,_#eef4f7_100%)]">
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-5 sm:px-6 lg:px-8">
        {view === 'list' ? (
          <>
            <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white/90 shadow-[0_20px_50px_-34px_rgba(15,23,42,0.35)] backdrop-blur">
              <div className="grid gap-4 px-4 py-4 sm:px-6 sm:py-5 lg:grid-cols-[1.6fr_1fr]">
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-700">
                    Gerenciamento de Mandados
                  </div>
                  <div className="space-y-2">
                    <h1 className="max-w-2xl text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Area de Mandados - IA</h1>
                    <p className="max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">Simplifique a compreensao de mandados com Inteligencia Artificial.</p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button type="button" onClick={() => setView('upload')} className="inline-flex items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-teal-600/20 transition hover:bg-teal-700">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                      Adicionar Mandado (PDF)
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                  <div className="relative flex-1">
                    <svg className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="7" />
                      <path d="m20 20-3.5-3.5" />
                    </svg>
                    <input value={search} onChange={(event) => { setSearch(event.target.value); setCurrentPage(1); }} placeholder="Buscar por processo, parte ou regiao..." className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-12 pr-4 text-sm text-slate-700 outline-none transition focus:border-teal-400 focus:bg-white focus:ring-4 focus:ring-teal-100" />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-[minmax(0,220px)_auto_auto]">
                    <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as 'Todos' | MandateStatus); setCurrentPage(1); }} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-400 focus:ring-4 focus:ring-teal-100">
                      {statusOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                    <button type="button" onClick={() => setShowAdvancedFilters((current) => !current)} className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 6h16M7 12h10M10 18h4" />
                      </svg>
                      Filtros Avancados
                    </button>
                    <button type="button" onClick={resetFilters} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-500 transition hover:bg-slate-50">
                      Limpar
                    </button>
                  </div>
                </div>

                {showAdvancedFilters && (
                  <div className="grid gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2 xl:grid-cols-4">
                    <label className="space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Data</span>
                      <input type="date" value={dateFilter} onChange={(event) => { setDateFilter(event.target.value); setCurrentPage(1); }} className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-400 focus:ring-4 focus:ring-teal-100" />
                    </label>
                    <label className="space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Prioridade</span>
                      <select value={priorityFilter} onChange={(event) => { setPriorityFilter(event.target.value as 'Todas' | MandatePriority); setCurrentPage(1); }} className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-400 focus:ring-4 focus:ring-teal-100">
                        {priorityOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </label>
                    <label className="space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Tipo de mandado</span>
                      <select value={typeFilter} onChange={(event) => { setTypeFilter(event.target.value as 'Todos' | MandateType); setCurrentPage(1); }} className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-400 focus:ring-4 focus:ring-teal-100">
                        {typeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </label>
                    <label className="space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Regiao</span>
                      <select value={regionFilter} onChange={(event) => { setRegionFilter(event.target.value); setCurrentPage(1); }} className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-400 focus:ring-4 focus:ring-teal-100">
                        {regions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </label>
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-[28px] border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-4 sm:px-6">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Fila inteligente de mandados</h2>
                  <p className="text-sm text-slate-500">Clique em uma fila para abrir a lista completa.</p>
                </div>
                <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                  {isLoadingMandates ? 'Carregando...' : `${queueGroups.length} fila(s)`}
                </div>
              </div>

              <div className="grid gap-4 border-b border-slate-100 p-4 sm:grid-cols-2 xl:grid-cols-3 sm:p-6">
                {queueGroups.map((queue) => {
                  const isActive = queue.attachmentDate === activeQueueDate;

                  return (
                    <button
                      key={queue.attachmentDate}
                      type="button"
                      onClick={() => {
                        setSelectedQueueDate(queue.attachmentDate);
                        setCurrentPage(1);
                      }}
                      className={`rounded-[24px] border p-5 text-left transition hover:-translate-y-0.5 hover:shadow-md ${
                        isActive ? 'border-teal-300 bg-teal-50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Data de anexacao</p>
                          <h3 className="mt-2 text-lg font-semibold text-slate-900">{formatLongDate(queue.attachmentDate)}</h3>
                        </div>
                        <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                          {queue.total} mandado(s)
                        </span>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                          Urgentes: {queue.urgent}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">
                    {activeQueueDate ? `Lista completa da fila de ${formatLongDate(activeQueueDate)}` : 'Selecione uma fila para ver a lista completa'}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {activeQueueDate ? `${visibleMandates.length} mandado(s) nesta data de anexacao.` : 'A listagem completa aparece aqui apos selecionar uma fila.'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {activeQueueDate && (
                    <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
                      <span>Exibir</span>
                      <select
                        value={itemsPerPage}
                        onChange={(event) => setItemsPerPage(Number(event.target.value))}
                        className="bg-transparent text-sm font-medium text-slate-700 outline-none"
                      >
                        {[5, 10, 20, 50, 100].map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {activeQueueDate && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedQueueDate(null);
                        setCurrentPage(1);
                      }}
                      className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-600 transition hover:bg-slate-50"
                    >
                      Voltar para todas as filas
                    </button>
                  )}
                </div>
              </div>

              {activeQueueDate && (
                <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                    <span className="rounded-full bg-slate-100 px-3 py-1 font-medium">
                      {selectedMandateIds.length} mandado(s) selecionado(s)
                    </span>
                    <span className="rounded-full bg-teal-50 px-3 py-1 font-medium text-teal-700">
                      {visibleMandates.length} na fila aberta
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={toggleVisibleSelection}
                      className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                    >
                      {selectedVisibleCount === visibleMandates.length && visibleMandates.length > 0
                        ? 'Desmarcar fila'
                        : 'Selecionar fila inteira'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedMandateIds([])}
                      disabled={selectedMandateIds.length === 0}
                      className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Limpar selecao
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowBulkComposer(true)}
                      disabled={selectedMandateIds.length === 0}
                      className="rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Criar envio em massa
                    </button>
                  </div>
                </div>
              )}

              {activeQueueDate ? (
                <>
              <div className="hidden overflow-x-auto lg:block">
                <table className="min-w-full divide-y divide-slate-100">
                  <thead className="bg-slate-50/80">
                    <tr className="text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      <th className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={visibleMandates.length > 0 && selectedVisibleCount === visibleMandates.length}
                          onChange={toggleVisibleSelection}
                          className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                          aria-label="Selecionar mandados da fila"
                        />
                      </th>
                      <th className="px-6 py-4">Parte</th>
                      <th className="px-6 py-4">Processo</th>
                      <th className="px-6 py-4">Telefone(s)</th>
                      <th className="px-6 py-4">Mandado</th>
                      <th className="px-6 py-4">Datas</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Acoes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentItems.map((mandate) => {
                      const days = getDaysUntil(mandate.hearingDate);
                      const isCritical = days <= 3 && days >= 0;
                      const isExpired = days < 0;

                      return (
                        <tr key={mandate.id} className={`transition hover:bg-slate-50 ${isCritical ? 'bg-amber-50/50' : ''} ${isExpired ? 'bg-rose-50/40' : ''}`}>
                          <td className="px-6 py-5 align-top">
                            <input
                              type="checkbox"
                              checked={selectedMandateIds.includes(mandate.id)}
                              onChange={() => toggleMandateSelection(mandate.id)}
                              className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                              aria-label={`Selecionar ${mandate.recipientName}`}
                            />
                          </td>
                          <td className="px-6 py-5 align-top">
                            <div className="space-y-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold text-slate-900">{mandate.recipientName}</span>
                                {mandate.confidential && <span className="rounded-full bg-violet-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-violet-700">Sigilo</span>}
                              </div>
                              <p className="max-w-md text-sm leading-6 text-slate-600">{mandate.address}</p>
                              <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">{mandate.region}</p>
                            </div>
                          </td>
                          <td className="px-6 py-5 align-top">
                            <div className="space-y-2 text-sm text-slate-700">
                              <p className="font-semibold">{mandate.caseNumber}</p>
                              <p className="text-slate-500">Anexado: {formatDate(mandate.attachmentDate)}</p>
                            </div>
                          </td>
                          <td className="px-6 py-5 align-top">
                            <div className="space-y-3">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
                                  {mandate.phones?.length || 0} telefone(s)
                                </span>
                                {!editingPhonesByMandate[mandate.id] && (
                                  <button
                                    type="button"
                                    onClick={() => startPhoneEditing(mandate)}
                                    className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                                  >
                                    Editar
                                  </button>
                                )}
                              </div>

                              {editingPhonesByMandate[mandate.id] ? (
                                <div className="space-y-2">
                                  {getEditablePhones(mandate).map((phone, index) => (
                                    <div key={`${mandate.id}-phone-${index}`} className="flex items-center gap-2">
                                      <input
                                        value={phone}
                                        onChange={(event) => updateEditablePhone(mandate.id, index, event.target.value)}
                                        placeholder="+55..."
                                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-teal-400 focus:ring-4 focus:ring-teal-100"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => removeEditablePhone(mandate.id, index)}
                                        className="rounded-full border border-rose-200 p-2 text-rose-700 transition hover:bg-rose-50"
                                        aria-label="Remover telefone"
                                      >
                                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M18 6 6 18M6 6l12 12" />
                                        </svg>
                                      </button>
                                    </div>
                                  ))}
                                  <div className="flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      onClick={() => addEditablePhone(mandate.id)}
                                      className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                                    >
                                      + Adicionar telefone
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => saveMandatePhones(mandate)}
                                      disabled={savingPhonesByMandate[mandate.id]}
                                      className="rounded-full bg-teal-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-teal-700 disabled:opacity-60"
                                    >
                                      {savingPhonesByMandate[mandate.id] ? 'Salvando...' : 'Salvar'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => cancelPhoneEditing(mandate.id)}
                                      className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {mandate.phones && mandate.phones.length > 0 ? (
                                    mandate.phones.map((phone, index) => (
                                      <div key={`${mandate.id}-phone-view-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                                        {phone}
                                      </div>
                                    ))
                                  ) : (
                                    <p className="text-sm text-slate-400">Sem telefone informado</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-5 align-top">
                            <div className="space-y-2">
                              <span className="block text-sm font-medium text-slate-800">{mandate.mandateType}</span>
                              <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getPriorityClass(mandate.priority)}`}>{mandate.priority}</span>
                            </div>
                          </td>
                          <td className="px-6 py-5 align-top">
                            <div className="space-y-2 text-sm text-slate-700">
                              <p>Mandado: {formatDate(mandate.mandateDate)}</p>
                              <p>Audiencia: {formatDate(mandate.hearingDate)}</p>
                              <div>{renderCountdown(mandate.hearingDate)}</div>
                            </div>
                          </td>
                          <td className="px-6 py-5 align-top">
                            <div className="space-y-2">
                              <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusClass(mandate.status)}`}>{mandate.status}</span>
                              {isCritical && <p className="text-xs font-semibold text-amber-700">Priorizar contato hoje</p>}
                              {isExpired && <p className="text-xs font-semibold text-rose-700">Prazo vencido</p>}
                            </div>
                          </td>
                          <td className="px-6 py-5 align-top">{renderMandateActions(mandate)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-4 p-4 lg:hidden">
                {currentItems.map((mandate) => {
                  const days = getDaysUntil(mandate.hearingDate);
                  const isCritical = days <= 3 && days >= 0;
                  const isExpired = days < 0;

                  return (
                    <article key={mandate.id} className={`rounded-3xl border p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${isExpired ? 'border-rose-200 bg-rose-50/70' : isCritical ? 'border-amber-200 bg-amber-50/70' : 'border-slate-200 bg-white'}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex-1">
                          <label className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                            <input
                              type="checkbox"
                              checked={selectedMandateIds.includes(mandate.id)}
                              onChange={() => toggleMandateSelection(mandate.id)}
                              className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                            />
                            Selecionar para envio
                          </label>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-base font-semibold text-slate-900">{mandate.recipientName}</h3>
                            {mandate.confidential && <span className="rounded-full bg-violet-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-violet-700">Sigilo</span>}
                          </div>
                          <p className="mt-2 text-sm leading-6 text-slate-600">{mandate.address}</p>
                          <div className="mt-3 space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
                                {mandate.phones?.length || 0} telefone(s)
                              </span>
                              {!editingPhonesByMandate[mandate.id] && (
                                <button
                                  type="button"
                                  onClick={() => startPhoneEditing(mandate)}
                                  className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                                >
                                  Editar
                                </button>
                              )}
                            </div>
                            {editingPhonesByMandate[mandate.id] ? (
                              <div className="space-y-2">
                                {getEditablePhones(mandate).map((phone, index) => (
                                  <div key={`${mandate.id}-mobile-phone-${index}`} className="flex items-center gap-2">
                                    <input
                                      value={phone}
                                      onChange={(event) => updateEditablePhone(mandate.id, index, event.target.value)}
                                      placeholder="+55..."
                                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-teal-400 focus:ring-4 focus:ring-teal-100"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeEditablePhone(mandate.id, index)}
                                      className="rounded-full border border-rose-200 p-2 text-rose-700 transition hover:bg-rose-50"
                                    >
                                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M18 6 6 18M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                ))}
                                <div className="flex flex-wrap gap-2">
                                  <button type="button" onClick={() => addEditablePhone(mandate.id)} className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-50">
                                    + Adicionar telefone
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => saveMandatePhones(mandate)}
                                    disabled={savingPhonesByMandate[mandate.id]}
                                    className="rounded-full bg-teal-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-teal-700 disabled:opacity-60"
                                  >
                                    {savingPhonesByMandate[mandate.id] ? 'Salvando...' : 'Salvar'}
                                  </button>
                                  <button type="button" onClick={() => cancelPhoneEditing(mandate.id)} className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50">
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {mandate.phones && mandate.phones.length > 0 ? mandate.phones.map((phone, index) => (
                                  <div key={`${mandate.id}-mobile-phone-view-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                                    {phone}
                                  </div>
                                )) : (
                                  <p className="text-sm text-slate-400">Sem telefone informado</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusClass(mandate.status)}`}>{mandate.status}</span>
                      </div>

                      <div className="mt-4 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Anexacao</p>
                          <p className="mt-1 font-medium">{formatDate(mandate.attachmentDate)}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Processo</p>
                          <p className="mt-1 font-medium">{mandate.caseNumber}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Tipo</p>
                          <p className="mt-1 font-medium">{mandate.mandateType}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Prioridade</p>
                          <span className={`mt-1 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getPriorityClass(mandate.priority)}`}>{mandate.priority}</span>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Audiencia</p>
                          <p className="mt-1 font-medium">{formatDate(mandate.hearingDate)}</p>
                        </div>
                      </div>

                      <div className="mt-4 rounded-2xl bg-slate-50 px-3 py-3 text-sm">{renderCountdown(mandate.hearingDate)}</div>

                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{mandate.region}</span>
                        {renderMandateActions(mandate)}
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <p className="text-sm text-slate-500">Pagina {currentPage} de {totalPages}</p>
                <div className="flex items-center gap-2">
                  <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">Anterior</button>
                  <button type="button" disabled={currentPage === totalPages} onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">Proxima</button>
                </div>
              </div>
                </>
              ) : (
                <div className="px-4 py-10 text-center text-sm text-slate-500 sm:px-6">
                  Escolha uma fila por data de anexacao para abrir a lista completa de mandados daquele lote.
                </div>
              )}
            </section>

            <section className="rounded-[28px] border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-4 sm:px-6">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Acoes de envio em massa</h2>
                  <p className="text-sm text-slate-500">Campanhas criadas a partir da selecao de mandados desta area.</p>
                </div>
                <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                  {isLoadingCampaigns ? 'Carregando...' : `${mandateCampaigns.length} acao(oes)`}
                </div>
              </div>

              {mandateCampaigns.length > 0 ? (
                <div className="grid gap-4 p-4 sm:grid-cols-2 sm:p-6 xl:grid-cols-3">
                  {mandateCampaigns.map((campaign) => (
                    <article key={campaign.id} className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Acao de envio</p>
                          <h3 className="mt-2 text-base font-semibold text-slate-900">{campaign.nome}</h3>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getCampaignStatusClass(campaign.status)}`}>
                            {getCampaignStatusLabel(campaign.status)}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedCampaigns = mandateCampaigns.filter((c) => c.id !== campaign.id);
                              setMandateCampaigns(updatedCampaigns);
                              toast.success('Acao de envio removida da lista.');
                            }}
                            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                            aria-label="Excluir acao"
                          >
                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 2H8C6.9 2 6 2.9 6 4v2H3v2h1v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8h1V6h-3V4c0-1.1-.9-2-2-2zM9 4h6v2H9V4zm9 14H6V8h12v10zM9 10h2v8H9zm4 0h2v8h-2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="mt-4 grid gap-3 text-sm text-slate-600 sm:grid-cols-2">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Mandados</p>
                          <p className="mt-1 font-medium text-slate-900">{campaign.totalContacts}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Mensagens</p>
                          <p className="mt-1 font-medium text-slate-900">{campaign._count?.messages ?? campaign.totalContacts}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Enviadas</p>
                          <p className="mt-1 font-medium text-emerald-700">{campaign.sentCount}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Falhas</p>
                          <p className="mt-1 font-medium text-rose-700">{campaign.failedCount}</p>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center justify-between gap-3">
                        <p className="text-xs font-medium uppercase tracking-[0.16em] text-slate-400">
                          Criada em {formatDate(String(campaign.criadoEm).slice(0, 10))}
                        </p>
                        <button
                          type="button"
                          onClick={() => navigate(`/campanhas/${campaign.id}`)}
                          className="rounded-xl bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-700 transition hover:bg-teal-100"
                        >
                          Ver relatorio
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-10 text-center text-sm text-slate-500 sm:px-6">
                  Nenhuma acao de envio em massa foi criada a partir dos Mandados IA ainda.
                </div>
              )}
            </section>
          </>
        ) : (
          <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
            <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-teal-700">Upload inteligente</p>
                  <h1 className="mt-2 text-2xl font-semibold text-slate-900 sm:text-3xl">Adicionar Mandado (PDF)</h1>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">Arraste o PDF aqui ou clique para enviar. A IA extrai os dados centrais e gera um resumo operacional claro.</p>
                </div>
                <button type="button" onClick={() => setView('list')} className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50">Voltar para lista</button>
              </div>

              <div className="mt-6 rounded-[28px] border-2 border-dashed border-teal-200 bg-[linear-gradient(135deg,_rgba(20,184,166,0.08),_rgba(255,255,255,1))] p-6 text-center sm:p-10">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-white shadow-sm">
                  <svg className="h-8 w-8 text-teal-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 3v12" />
                    <path d="m7 8 5-5 5 5" />
                    <path d="M20 21H4" />
                  </svg>
                </div>
                <p className="mt-4 text-lg font-semibold text-slate-900">Arraste o PDF aqui ou clique para enviar</p>
                <p className="mt-2 text-sm text-slate-500">Suporte para mandados com varias paginas e leitura de campos juridicos essenciais.</p>
                <label className="mt-6 inline-flex cursor-pointer items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
                  Selecionar arquivo
                      <input type="file" accept=".pdf" className="hidden" onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      setUploadFile(file);
                      setUploadFileName(file.name);
                      toast.success(`Arquivo ${file.name} pronto para leitura.`);
                    }
                  }} />
                </label>
                {uploadFileName && <p className="mt-4 text-sm font-medium text-slate-700">Arquivo selecionado: {uploadFileName}</p>}
                <button type="button" onClick={handleProcessPdf} disabled={isProcessing} className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70">
                  {isProcessing ? 'Lendo com IA...' : 'Ler com IA'}
                </button>
              </div>

            </div>

            <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Leitura Inteligente por IA</p>
                  <h2 className="mt-2 text-xl font-semibold text-slate-900">Resumo estruturado do documento</h2>
                </div>
                {isProcessing && <div className="flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-700"><span className="h-2 w-2 animate-pulse rounded-full bg-teal-500" />Processando</div>}
              </div>

                  {!processedDraft && !isProcessing && <div className="mt-6 rounded-[28px] border border-slate-200 bg-slate-50 p-6 text-sm leading-6 text-slate-500">Faca o upload do PDF para extrair automaticamente numero do processo, datas, destinatario, endereco, telefone(s), motivo do mandado, tipo de ato e resumo claro das acoes necessarias.</div>}

              {isProcessing && <div className="mt-6 space-y-4"><div className="h-24 animate-pulse rounded-3xl bg-slate-100" /><div className="h-16 animate-pulse rounded-3xl bg-slate-100" /><div className="h-32 animate-pulse rounded-3xl bg-slate-100" /></div>}

              {processedDraft && (
                <div className="mt-6 space-y-5">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-3xl border border-slate-200 p-4"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Numero do processo</p><p className="mt-2 text-sm font-semibold text-slate-900">{processedDraft.caseNumber}</p></div>
                    <div className="rounded-3xl border border-slate-200 p-4"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Tipo de ato</p><p className="mt-2 text-sm font-semibold text-slate-900">{processedDraft.mandateType}</p></div>
                    <div className="rounded-3xl border border-slate-200 p-4"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Data de distribuicao</p><p className="mt-2 text-sm font-semibold text-slate-900">{formatDate(processedDraft.distributionDate)}</p></div>
                    <div className="rounded-3xl border border-slate-200 p-4"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Data da audiencia</p><p className="mt-2 text-sm font-semibold text-slate-900">{formatDate(processedDraft.hearingDate)}</p><p className="mt-1 text-xs font-medium text-amber-700">{renderCountdown(processedDraft.hearingDate)}</p></div>
                    <div className="rounded-3xl border border-slate-200 p-4 sm:col-span-2"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Destinatario e endereco</p><p className="mt-2 text-sm font-semibold text-slate-900">{processedDraft.recipientName}</p><p className="mt-1 text-sm text-slate-600">{processedDraft.address}</p></div>
                    <div className="rounded-3xl border border-slate-200 p-4"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Telefone(s)</p><p className="mt-2 text-sm font-semibold text-slate-900">{processedDraft.phones.length > 0 ? processedDraft.phones.join(', ') : 'Nao localizado'}</p></div>
                    <div className="rounded-3xl border border-slate-200 p-4"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Motivo do mandado</p><p className="mt-2 text-sm font-semibold text-slate-900">{processedDraft.reason || 'Nao identificado'}</p></div>
                  </div>

                  <div className="rounded-[28px] bg-slate-900 p-5 text-white">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">Resumo do mandado</p>
                    <p className="mt-3 text-sm leading-7 text-slate-100">{processedDraft.summary}</p>
                    <p className="mt-4 rounded-2xl bg-white/10 px-4 py-3 text-sm text-slate-100"><span className="font-semibold">Acao necessaria:</span> {processedDraft.nextAction}</p>
                  </div>

                  <div className="rounded-[28px] border border-emerald-200 bg-emerald-50 p-5">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Resumo do mandado</p>
                      <button
                        type="button"
                        onClick={() => handleCopyMessage(draftSummary)}
                        className="rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100"
                      >
                        Copiar resumo
                      </button>
                    </div>
                    <textarea
                      value={draftSummary}
                      onChange={(event) => setDraftSummary(event.target.value)}
                      className="mt-3 min-h-[180px] w-full rounded-2xl border border-emerald-200 bg-white px-4 py-3 text-sm leading-6 text-emerald-900 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                    />
                    {processedDraft.provider && (
                      <p className="mt-2 text-xs font-medium uppercase tracking-[0.16em] text-emerald-700">Provider: {processedDraft.provider}</p>
                    )}
                  </div>

                  <div className="rounded-[28px] border border-slate-200 p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Mensagem pronta do oficial</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={handleUseSummaryInWhatsAppDraft}
                          className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                        >
                          Usar resumo na mensagem
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCopyMessage(draftWhatsAppMessage)}
                          className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                        >
                          Copiar mensagem
                        </button>
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="space-y-2">
                        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Mensagens salvas de envio</span>
                        <select
                          value={draftTemplateId}
                          onChange={(event) => handleApplyTemplateToDraftMessage(event.target.value)}
                          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-400 focus:ring-4 focus:ring-teal-100"
                        >
                          <option value="">Usar texto atual da leitura</option>
                          {savedTemplates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.title}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <textarea
                      value={draftWhatsAppMessage}
                      onChange={(event) => setDraftWhatsAppMessage(event.target.value)}
                      className="mt-3 min-h-[150px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700 outline-none transition focus:border-teal-400 focus:bg-white focus:ring-4 focus:ring-teal-100"
                    />
                  </div>

                  <button type="button" onClick={handleCreateFromDraft} className="w-full rounded-2xl bg-teal-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-teal-700">Salvar mandado e voltar para a fila</button>
                </div>
              )}
            </div>
          </section>
        )}
      </div>

      {detailsMandate && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/45 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[32px] bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-teal-700">Visualizar detalhes</p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">{detailsMandate.recipientName}</h2>
                <p className="mt-2 text-sm text-slate-500">{detailsMandate.caseNumber}</p>
              </div>
              <button type="button" onClick={() => setDetailsMandate(null)} className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50" aria-label="Fechar detalhes">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M6 18 18 6" /></svg>
              </button>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-3xl border border-slate-200 p-4"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Endereco completo</p><p className="mt-2 text-sm leading-6 text-slate-700">{detailsMandate.address}</p></div>
              <div className="rounded-3xl border border-slate-200 p-4"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Tipo e prioridade</p><p className="mt-2 text-sm font-semibold text-slate-900">{detailsMandate.mandateType}</p><span className={`mt-3 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getPriorityClass(detailsMandate.priority)}`}>{detailsMandate.priority}</span></div>
              <div className="rounded-3xl border border-slate-200 p-4"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Datas</p><p className="mt-2 text-sm text-slate-700">Mandado: {formatDate(detailsMandate.mandateDate)}</p><p className="mt-1 text-sm text-slate-700">Audiencia: {formatDate(detailsMandate.hearingDate)}</p><p className="mt-2 text-sm font-medium text-amber-700">{renderCountdown(detailsMandate.hearingDate)}</p></div>
              <div className="rounded-3xl border border-slate-200 p-4"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Status e sigilo</p><span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusClass(detailsMandate.status)}`}>{detailsMandate.status}</span><p className="mt-3 text-sm text-slate-700">{detailsMandate.confidential ? 'Mandado com sigilo ativo.' : 'Mandado sem restricao de sigilo.'}</p></div>
            </div>

            <div className="mt-5 rounded-[28px] bg-slate-900 p-5 text-white">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">Resumo gerado por IA</p>
              <p className="mt-3 text-sm leading-7 text-slate-100">{detailsMandate.aiSummary}</p>
            </div>
          </div>
        </div>
      )}

      {showBulkComposer && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/45 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[32px] bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-teal-700">Envio em massa</p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">Criar acao a partir dos mandados selecionados</h2>
                <p className="mt-2 text-sm text-slate-500">{selectedMandates.length} mandado(s) serao vinculados a esta acao.</p>
              </div>
              <button type="button" onClick={() => setShowBulkComposer(false)} className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50" aria-label="Fechar envio em massa">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M6 18 18 6" /></svg>
              </button>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Conexao WhatsApp</span>
                <select
                  value={bulkSessionName}
                  onChange={(event) => setBulkSessionName(event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-400 focus:ring-4 focus:ring-teal-100"
                >
                  <option value="">Selecione uma conexao</option>
                  {whatsAppSessions.map((session) => (
                    <option key={session.name} value={session.name}>
                      {session.displayName || session.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Modelo de mensagem</span>
                <select
                  value={bulkTemplateId}
                  onChange={(event) => handleApplyTemplateToBulkComposer(event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-400 focus:ring-4 focus:ring-teal-100"
                >
                  <option value="">Usar texto atual sugerido</option>
                  {savedTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.title}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="mt-4 rounded-[28px] border border-slate-200 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Mensagem base da acao</p>
              <textarea
                value={bulkMessageDraft}
                onChange={(event) => setBulkMessageDraft(event.target.value)}
                className="mt-3 min-h-[180px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700 outline-none transition focus:border-teal-400 focus:bg-white focus:ring-4 focus:ring-teal-100"
              />
              <p className="mt-3 text-xs text-slate-500">
                As variaveis como {'{{nome}}'}, {'{{processo}}'} e {'{{mandado}}'} serao personalizadas automaticamente para cada destinatario.
              </p>
            </div>

            <div className="mt-4 rounded-[28px] bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Mandados selecionados</p>
              <div className="mt-3 grid gap-2">
                {selectedMandates.map((mandate) => (
                  <div key={mandate.id} className="flex items-center justify-between gap-3 rounded-2xl bg-white px-4 py-3 text-sm">
                    <div>
                      <p className="font-semibold text-slate-900">{mandate.recipientName}</p>
                      <p className="text-slate-500">{mandate.caseNumber}</p>
                    </div>
                    <div className="flex max-w-[55%] flex-wrap justify-end gap-2">
                      {mandate.phones && mandate.phones.length > 0 ? mandate.phones.map((phone, index) => (
                        <span key={`${mandate.id}-selected-phone-${index}`} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                          {phone}
                        </span>
                      )) : (
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                          Sem telefone
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setShowBulkComposer(false)} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
                Cancelar
              </button>
              <button
                type="button"
                disabled={isCreatingBulkAction}
                onClick={async () => {
                  const success = await createMandateCampaignAction({
                    mandateIds: selectedMandateIds,
                    message: bulkMessageDraft,
                    sessionName: bulkSessionName,
                    nome: `Mandados IA · Lote ${formatDate(activeQueueDate || new Date().toISOString().slice(0, 10))}`
                  });

                  if (success) {
                    setShowBulkComposer(false);
                    setSelectedMandateIds([]);
                    await loadMandateCampaigns();
                    const campaigns = await apiService.listCampaigns({ page: 1, limit: 20 });
                    const allCampaigns = Array.isArray(campaigns?.campaigns) ? campaigns.campaigns : [];
                    const mandateOnly = allCampaigns.filter((campaign: MandateCampaignAction) => campaign.nome.startsWith('Mandados IA'));
                    if (mandateOnly.length > 0) {
                      const latestCampaign = mandateOnly[0];
                      setTimeout(() => {
                        navigate(`/campanhas/${latestCampaign.id}`);
                      }, 1000);
                    }
                  }
                }}
                className="rounded-2xl bg-teal-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Criar acao de envio em massa
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedMandate && (
        <div className="fixed inset-y-0 right-0 z-50 flex w-full justify-end bg-slate-950/35">
          <button type="button" className="hidden flex-1 cursor-default lg:block" onClick={() => setSelectedMandate(null)} aria-label="Fechar painel" />
          <aside className="h-full w-full max-w-xl overflow-y-auto border-l border-slate-200 bg-white p-5 shadow-2xl sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">Enviar mensagem</p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">WhatsApp do mandado</h2>
                <p className="mt-2 text-sm text-slate-500">{selectedMandate.recipientName}</p>
              </div>
              <button type="button" onClick={() => setSelectedMandate(null)} className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50" aria-label="Fechar painel">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M6 18 18 6" /></svg>
              </button>
            </div>

            <div className="mt-6 space-y-5">
              <div className="rounded-[28px] border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Conexao WhatsApp</p>
                <select
                  value={selectedSessionName}
                  onChange={(event) => setSelectedSessionName(event.target.value)}
                  className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                >
                  <option value="">Selecione uma conexao</option>
                  {whatsAppSessions.map((session) => (
                    <option key={session.name} value={session.name}>
                      {session.displayName || session.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-[28px] border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Texto salvo de envio</p>
                  <span className="text-xs font-medium text-slate-400">{savedTemplates.length} modelo(s)</span>
                </div>
                <select
                  value={selectedTemplateId}
                  onChange={(event) => handleApplyTemplateToWhatsApp(event.target.value)}
                  className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                >
                  <option value="">Usar mensagem atual do mandado</option>
                  {savedTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-[28px] border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Mensagem pronta</p>
                <textarea
                  value={messageDraft}
                  onChange={(event) => setMessageDraft(event.target.value)}
                  className="mt-3 min-h-[170px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-100"
                />
              </div>

              <div className="rounded-[28px] bg-emerald-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Resumo do mandado</p>
                  <button type="button" onClick={() => handleCopyMessage(selectedMandate.aiSummary)} className="rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100">
                    Copiar resumo inteligente
                  </button>
                </div>
                <p className="mt-3 text-sm leading-7 text-emerald-900">{selectedMandate.aiSummary}</p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <button type="button" onClick={() => handleCopyMessage(messageDraft)} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50">Copiar mensagem</button>
                <button
                  type="button"
                  disabled={isCreatingBulkAction}
                  onClick={async () => {
                    const success = await createMandateCampaignAction({
                      mandateIds: [selectedMandate.id],
                      message: messageDraft,
                      sessionName: selectedSessionName,
                      nome: `Mandados IA · ${selectedMandate.recipientName}`
                    });

                    if (success) {
                      setSelectedMandateIds((current) => Array.from(new Set([...current, selectedMandate.id])));
                      setSelectedMandate(null);
                    }
                  }}
                  className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Criar acao de envio
                </button>
                <button type="button" onClick={() => setMessageDraft(selectedMandate.messageTemplate || '')} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50">Restaurar texto</button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
